<template>
  <div
    v-editable="blok"
    class="banner"
    :class="blok.bannerStyle === 'contrast' ? 'banner-contrast' : 'banner-normal'"
  >
    <div
      class="banner-content"
      :class="blok.bannerStyle === 'contrast' ? 'banner-content-contrast' : 'banner-content-normal'"
    >
      <StoryblokRichText v-if="richTextBlok" :blok="richTextBlok" />
      <p v-else-if="plainText">{{ plainText }}</p>
    </div>
    <div class="banner-link">
      <Button :type="buttonType" @click.prevent="openButtonLink">
        {{ blok.buttonTitle }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { Button } from '@/common/components/ui';
import { CommonRoutesName } from '@/router/constants/routesName';
import StoryblokRichText from '@/features/storyblok/components/StoryblokRichText.vue';
import { type StoryblokBannerBlok, type StoryblokRichTextBlok } from '@/features/storyblok/types';
import {
  extractStorySlugFromWardPath,
  resolveStoryblokLink,
} from '@/features/storyblok/utils/link';

const props = defineProps<{
  blok: StoryblokBannerBlok;
}>();

const buttonType = computed(() => (props.blok.bannerStyle === 'contrast' ? 'standard' : 'primary'));

const richTextBlok = computed<StoryblokRichTextBlok | null>(() => {
  const rt = props.blok.richText;
  if (!rt || typeof rt === 'string') return null;
  return { content: rt };
});

const plainText = computed(() =>
  typeof props.blok.richText === 'string' ? props.blok.richText.trim() : undefined
);

const router = useRouter();

const buttonTarget = computed(() => resolveStoryblokLink(props.blok.url, props.blok.linkType));

const openButtonLink = () => {
  if (!buttonTarget.value) {
    return;
  }

  if (buttonTarget.value.type === 'external') {
    window.open(buttonTarget.value.href, '_blank', 'noopener,noreferrer');
    return;
  }

  if (buttonTarget.value.type === 'storyblok') {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: buttonTarget.value.slug },
    });
    return;
  }

  const storySlugFromInternalPath = extractStorySlugFromWardPath(buttonTarget.value.path);
  if (storySlugFromInternalPath) {
    void router.replace({
      name: CommonRoutesName.StoryblokPage,
      query: { slug: storySlugFromInternalPath },
    });
    return;
  }

  void router.push(buttonTarget.value.path);
};
</script>

<style scoped>
.banner {
  display: grid;
  grid-template-columns: 6fr 1fr;
  padding: var(--ion-space-8) var(--ion-space-6);
  border-radius: var(--ion-radius-2xl);
  align-items: center;
}

@media (max-width: 768px) {
  .banner {
    grid-template-columns: 1fr;
    gap: var(--ion-space-4);
    padding: var(--ion-space-6) var(--ion-space-4);
  }
}
.banner-contrast {
  background-color: var(--ion-color-primary);
  color: var(--ion-text-color-contrast);
  border: 1px solid var(--ion-color-primary);
}
.banner-normal {
  background-color: var(--ion-color-contrast);
  color: var(--ion-text-color);
  border: 1px solid var(--ion-color-primary);
}

.banner-content-contrast {
  color: var(--ion-text-color-contrast);
}
.banner-content-normal {
  color: var(--ion-text-color);
}

.banner-content-contrast :deep(p),
.banner-content-contrast :deep(h1),
.banner-content-contrast :deep(h2),
.banner-content-contrast :deep(h3),
.banner-content-contrast :deep(h4),
.banner-content-contrast :deep(h5),
.banner-content-contrast :deep(h6) {
  margin: 0;
  color: var(--ion-text-color-contrast);
}

.banner-content-normal :deep(p),
.banner-content-normal :deep(h1),
.banner-content-normal :deep(h2),
.banner-content-normal :deep(h3),
.banner-content-normal :deep(h4),
.banner-content-normal :deep(h5),
.banner-content-normal :deep(h6) {
  margin: 0;
  color: var(--ion-text-color);
}

.banner-content-normal :deep(a) {
  color: var(--ion-text-color);
}

.banner-content-contrast :deep(a) {
  color: var(--ion-text-color-contrast);
}
</style>
